/* eslint-disable react/no-danger */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

import IsServerSideRendering from '../../../../utilities/IsServerSideRendering';
import ApplicationSettings from '../../../../services/ApplicationSettings';
import Video from '../../../../Shared/Video';
import Footnote from './Footnote';
import {
  PromotionContentWrapper,
  SectionsWrapper,
  HeaderTagLine,
  HeaderHero,
  SubHeader,
  VideoWrapper,
  Image,
  Button,
  Link,
  CalloutWrapper,
  CalloutInfoWrapper,
  CalloutTitle,
  CalloutInfo,
  Body
} from './ContentStyles';

const getLinkAttributes = (link, handleCloseModal) => {
  if (IsServerSideRendering) {
    return {
      href: link,
      target: '_self',
    };
  }

  const isLinkForCurrentPage = window.location.pathname === link;
  if (isLinkForCurrentPage) {
    return {
      href: '#',
      onClick: handleCloseModal,
    };
  }

  return {
    href: link,
    target: '_self',
  };
};

const RenderSection = (section, cdnUrl, handleCloseModal) => {
  switch (section.type) {
    case 'tagline':
      return (
        <HeaderTagLine css={section.css}>{section.message}</HeaderTagLine>
      );
    case 'header':
      return (
        <HeaderHero css={section.css}>{section.message}</HeaderHero>
      );
    case 'subheader':
      return (
        <SubHeader css={section.css}>{section.message}</SubHeader>
      );
    case 'video':
      return (
        <VideoWrapper css={section.css}>
          <Video
            imageUrl={`${cdnUrl}/${section.imageUrl}`}
            youtubeId={section.youtubeId}
          />
        </VideoWrapper>
      );
    case 'body': {
      const cleanHtml = DOMPurify.sanitize(section.message);
      return (
        <Body cssOverrides={section.css}>
          <p dangerouslySetInnerHTML={{ __html: cleanHtml }} />
        </Body>
      );
    }
    case 'image':
      return (
        <Fragment>
          <Image
            className="desktop"
            css={section.css}
            src={`${cdnUrl}/${section.desktop || section.imageUrl}`}
            alt={section.alt}
          />
          <Image
            className="tablet"
            css={section.css}
            src={`${cdnUrl}/${section.tablet || section.imageUrl}`}
            alt={section.alt}
          />
          <Image
            className="mobile"
            css={section.css}
            src={`${cdnUrl}/${section.mobile || section.imageUrl}`}
            alt={section.alt}
          />
        </Fragment>
      );
    case 'button':
      return (
        <Button
          {...getLinkAttributes(section.link, handleCloseModal)}
          css={section.css}
          target={section.target || '_self'}
        >
          {section.message}
        </Button>
      );
    case 'link':
      return (
        <Link
          {...getLinkAttributes(section.link, handleCloseModal)}
          css={section.css}
        >
          {section.message}
        </Link>
      );
    case 'callout':
      return (
        <CalloutWrapper css={section.css}>
          <img
            src={`${cdnUrl}/${section.imageUrl}`}
            alt={section.alt}
            css={section.imageCss}
          />
          <CalloutInfoWrapper>
            <CalloutTitle css={section.titleCss}>{section.title}</CalloutTitle>
            <CalloutInfo css={section.infoCss}>{section.info}</CalloutInfo>
          </CalloutInfoWrapper>
        </CalloutWrapper>
      );
    default:
      return null;
  }
};

const PromotionContent = ({ content, handleCloseModal }) => {
  const cdnUrl = ApplicationSettings.getApplicationSetting('cdnFastlyUrl');

  return (
    <PromotionContentWrapper>
      <SectionsWrapper css={content.css}>
        {content.sections.map((section, index) => (
          // eslint-disable-next-line
          <Fragment key={`${section.type}-${index}`}>
            {RenderSection(section, cdnUrl, handleCloseModal)}
          </Fragment>
        ))}
      </SectionsWrapper>
      <Footnote footnote={content.footnote} />
    </PromotionContentWrapper>
  );
};

PromotionContent.propTypes = {
  content: PropTypes.shape({}).isRequired,
  handleCloseModal: PropTypes.func.isRequired,
};

export default PromotionContent;
